import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { analyticsService } from '../services/analyticsService';
import '../styles/HomePage.css';
import '../index.css';

function LoginPage() {
  const [view, setView] = useState('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const location = useLocation();

  // Set message from location state if available
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location.state]);

  // Password validation
  const hasMinLength = newPassword.length >= 8 || loginPassword.length >= 8;
  const hasLowercase = /[a-z]/.test(newPassword) || /[a-z]/.test(loginPassword);
  const hasUppercase = /[A-Z]/.test(newPassword) || /[A-Z]/.test(loginPassword);
  const hasNumber = /\d/.test(newPassword) || /\d/.test(loginPassword);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) || /[!@#$%^&*(),.?":{}|<>]/.test(loginPassword);
  
  const isPasswordValid = hasMinLength && hasLowercase && hasUppercase && hasNumber && hasSpecialChar;

  const { 
    login, 
    signUp, 
    verifyEmail, 
    forgotPassword, 
    confirmPassword,
    resendVerificationCode
  } = useAuth();
  
  const navigate = useNavigate();

  const setErrorAndScroll = (errorMessage) => {
    setError(errorMessage);
    window.scrollTo(0, 0);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      await login(loginEmail, loginPassword);
      analyticsService.trackEvent(loginEmail, 'login');
      const from = location.state?.from || '/';
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === 'UserNotConfirmedException') {
        setMessage('Please verify your email first.');
        setView('verify');
      } else {
        setErrorAndScroll(err.message || 'Failed to login');
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    try {
      await forgotPassword(forgotPasswordEmail);
      setMessage('If an account exists with this email, a password reset code will be sent.');
      setView('resetPassword');
    } catch (err) {
      // Don't expose whether the user exists or not for security
      setErrorAndScroll('Something went wrong. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      await confirmPassword(forgotPasswordEmail, resetCode, newPassword);
      setMessage('Password reset successful! You can now login.');
      setView('login');
      // Clear sensitive fields
      setResetCode('');
      setNewPassword('');
      setForgotPasswordEmail('');
    } catch (err) {
      setErrorAndScroll(err.message || 'Failed to reset password');
    }
  };

  const renderPasswordRequirements = (password) => {
    const currentPassword = view === 'resetPassword' ? newPassword : loginPassword;
    if (!currentPassword) return null;
    
    const requirements = [
      { met: currentPassword.length >= 8, text: "At least 8 characters" },
      { met: /[a-z]/.test(currentPassword), text: "One lowercase letter" },
      { met: /[A-Z]/.test(currentPassword), text: "One uppercase letter" },
      { met: /\d/.test(currentPassword), text: "One number" },
      { met: /[!@#$%^&*(),.?":{}|<>]/.test(currentPassword), text: "One special character" }
    ];
    
    return (
      <div className="container" style={{ padding: '20px', textAlign: 'left', margin: 0 }}>
        <h4>Password Requirements:</h4>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          {requirements.map((req, index) => (
            <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ 
                color: req.met ? 'green' : 'red', 
                marginRight: '0.5rem',
                fontWeight: 'bold'
              }}>
                {req.met ? '✓' : '✗'}
              </span>
              {req.text}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderForgotPassword = () => (
    <>
      <h1>Reset Password</h1>
      <form onSubmit={handleForgotPassword}>
        <div className="form-group">
          <label htmlFor="forgotPasswordEmail">Email:</label>
          <input
            type="email"
            id="forgotPasswordEmail"
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="button standout-button">
          Send Reset Code
        </button>
        <button 
          type="button"
          onClick={() => setView('login')} 
          className="button secondary-button"
        >
          Back to Login
        </button>
      </form>
    </>
  );

  const renderResetPassword = () => (
    <>
      <h1>Enter Reset Code</h1>
      <p>A reset code has been sent to: {forgotPasswordEmail}</p>
      <form onSubmit={handleResetPassword}>
        <div className="form-group">
          <label htmlFor="resetCode">Reset Code:</label>
          <input
            type="text"
            id="resetCode"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            required
          />
          
          <label htmlFor="newPassword">New Password:</label>
            <input
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              style={{
                position: 'relative',
                transform: 'translate(400%, -175%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
              title={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? '🙉' : '🙈'}
            </button>
          
          {renderPasswordRequirements(newPassword)}
        </div>
        
        <button 
          type="submit" 
          className="button standout-button"
          disabled={!isPasswordValid}
        >
          Reset Password
        </button>
        <button 
          type="button"
          onClick={() => setView('forgotPassword')} 
          className="button secondary-button"
        >
          Back
        </button>
      </form>
    </>
  );

  const renderLogin = () => (
    <>
      <h1>Please Login:</h1>
      <form onSubmit={handleLogin}>
        <div className="form-group">
          <label htmlFor="loginEmail">Email:</label>
          <input 
            type="email" 
            id="loginEmail" 
            value={loginEmail} 
            onChange={(e) => setLoginEmail(e.target.value)} 
            required 
          />
          
          <label htmlFor="loginPassword">Password:</label>
          <div>
            <input 
              type={showPassword ? "text" : "password"} 
              id="loginPassword" 
              value={loginPassword} 
              onChange={(e) => setLoginPassword(e.target.value)} 
              required 
              style={{ width: '300px' }}
            />
          </div>

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'relative',
                right: '0px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? '🙉' : '🙈'}
            </button>
        </div>
        <button type="submit" className="button standout-button">
          Login
        </button>
      </form>
      <p>
        Don't have an account?{' '}
        <button onClick={() => setView('register')} className="link-button">
          Register here
        </button>
      </p>
      <p>
        <button onClick={() => setView('forgotPassword')} className="link-button">
          Forgot Password?
        </button>
      </p>
    </>
  );

  const renderRegister = () => (
    <>
      <h1>Create Account:</h1>
      <form onSubmit={handleRegister}>
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input 
            type="text" 
            id="firstName" 
            value={firstName} 
            onChange={(e) => setFirstName(e.target.value)} 
            required 
          />
          
          <label htmlFor="lastName">Last Name:</label>
          <input 
            type="text" 
            id="lastName" 
            value={lastName} 
            onChange={(e) => setLastName(e.target.value)} 
            required 
          />
          
          <label htmlFor="loginEmail">Email:</label>
          <input 
            type="email" 
            id="loginEmail" 
            value={loginEmail} 
            onChange={(e) => setLoginEmail(e.target.value)} 
            required 
          />
          
          <label htmlFor="loginPassword">Password:</label>
            <input 
              type={showPassword ? "text" : "password"} 
              id="loginPassword" 
              value={loginPassword} 
              onChange={(e) => setLoginPassword(e.target.value)} 
              required 
              style={{ width: '400px' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'relative',
                right: '5px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? '🙉' : '🙈'}
            </button>
          
          {renderPasswordRequirements(loginPassword)}
        </div>
        
        <button 
          type="submit" 
          className="button standout-button"
          disabled={!isPasswordValid && loginPassword.length > 0}
        >
          Register
        </button>
      </form>
      <p>
        Already have an account?{' '}
        <button onClick={() => setView('login')} className="link-button">
          Login here
        </button>
      </p>
    </>
  );

  const handleRegister = async (event) => {
    event.preventDefault();
    setError('');
    
    try {
      await signUp(loginEmail, loginPassword, firstName, lastName);
      analyticsService.trackEvent(loginEmail, 'signup');
      setMessage('Registration successful! Please check your email for verification code.');
      setView('verify');
    } catch (err) {
      if (err.code === 'UsernameExistsException') {
        try {
          await resendVerificationCode(loginEmail);
          setMessage('Account exists but not verified. A new verification code has been sent.');
          setView('verify');
        } catch (resendErr) {
          setErrorAndScroll('Error resending verification code. Please try again.');
        }
      } else {
        setErrorAndScroll(err.message || 'Failed to register');
      }
    }
  };

  const renderVerification = () => (
    <>
      <h1>Verify Email:</h1>
      <p>A verification code has been sent to: {loginEmail}</p>
      <form onSubmit={handleVerification}>
        <div className="form-group">
          <label htmlFor="verificationCode">Verification Code:</label>
          <input 
            type="text" 
            id="verificationCode" 
            value={verificationCode} 
            onChange={(e) => setVerificationCode(e.target.value)} 
            required 
          />
        </div>
        <div className="button-group">
          <button type="submit" className="button standout-button">
            Verify Email
          </button>
          <button 
            type="button" 
            onClick={handleResendCode} 
            className="button secondary-button"
          >
            Resend Code
          </button>
          <button 
            type="button" 
            onClick={() => setView('login')} 
            className="link-button"
          >
            Back to Login
          </button>
        </div>
      </form>
    </>
  );

  const handleVerification = async (event) => {
    event.preventDefault();
    setError('');
    
    try {
      await verifyEmail(loginEmail, verificationCode);
      setMessage('Email verified successfully! You can now login.');
      setView('login');
    } catch (err) {
      setErrorAndScroll(err.message || 'Failed to verify email');
    }
  };

  const handleResendCode = async () => {
    try {
      await resendVerificationCode(loginEmail);
      setMessage('Verification code resent! Please check your email.');
    } catch (err) {
      setErrorAndScroll(err.message || 'Failed to resend code');
    }
  };

  return (
    <div className="login-page">
      <div className="hero hero-1 top-hero">
        <div className="container">
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}
          
          {view === 'login' && renderLogin()}
          {view === 'register' && renderRegister()}
          {view === 'verify' && renderVerification()}
          {view === 'forgotPassword' && renderForgotPassword()}
          {view === 'resetPassword' && renderResetPassword()}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;